// web/src/settings/UsersPage.jsx
// ==============================================================================
// MIGRADO: DataGrid → Table + TablePagination
// Baseado em DevicesPage.jsx (modelo principal)
// ==============================================================================

import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CollectionActions from './components/CollectionActions';
import LoginIcon from '@mui/icons-material/Login';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import dayjs from 'dayjs';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useAdministrator } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync, useCatch } from '../reactHelper';
import { formatBoolean, formatTime } from '../common/util/formatter';
import useCrudExports from '../common/hooks/useCrudExports';


const UsersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();
  const theme = useTheme();
  const isAdmin = useAdministrator();

  const currentUser = useSelector((state) => state.session.user);

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ==============================================================================
  // 📡 FETCH DE DADOS
  // ==============================================================================
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/users?excludeAttributes=true');
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // ==============================================================================
  // 📊 ESTATÍSTICAS
  // ==============================================================================
  const stats = useMemo(() => {
    const active = items.filter((item) => !item.disabled).length;
    const disabled = items.filter((item) => item.disabled).length;
    return {
      total: items.length,
      active,
      disabled
    };
  }, [items]);

  // ==============================================================================
  // 🔍 FILTRO E BUSCA
  // ==============================================================================
  const filteredAndSearchedItems = useMemo(() => {
    let result = items;

    if (statusFilter === 'active') {
      result = result.filter((item) => !item.disabled);
    } else if (statusFilter === 'disabled') {
      result = result.filter((item) => item.disabled);
    }

    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter((item) => {
        return (
          item.name?.toLowerCase().includes(lower) ||
          item.email?.toLowerCase().includes(lower) ||
          item.id?.toString().includes(lower)
        );
      });
    }

    return result;
  }, [items, statusFilter, searchText]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSearchedItems.slice(start, start + rowsPerPage);
  }, [filteredAndSearchedItems, page, rowsPerPage]);

  // ==============================================================================
  // 📤 EXPORTAÇÕES
  // ==============================================================================
  const columns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'name', headerNameKey: 'sharedName' },
    { field: 'email', headerNameKey: 'userEmail' },
    { field: 'administrator', headerNameKey: 'userAdmin', valueGetter: ({ row }) => formatBoolean(row.administrator, t) },
    { field: 'disabled', headerNameKey: 'sharedDisabled', valueGetter: ({ row }) => formatBoolean(row.disabled, t) },
    {
      field: 'expirationTime',
      headerNameKey: 'userExpirationTime',
      valueGetter: ({ row }) => formatTime(row.expirationTime, 'date'),
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportUsersTitle',
    headerKey: 'reportUsersHeader',
    rows: filteredAndSearchedItems,
    columns,
    theme,
    t,
    options: {
      includeWatermark: true,
    },
  });

  // === FIX: login de administrador na conta de outro usuário (seguindo código original do Traccar) ===
  const handleLogin = useCatch(async (userId) => {
    await fetchOrThrow(`/api/session/${userId}`); // método GET — não POST
    window.location.replace('/'); // recarrega a sessão como o novo usuário
  });

  const actionLogin = {
    key: 'login',
    title: t('loginLogin'),
    icon: <LoginIcon fontSize="small" />,
    handler: handleLogin,
  };
  // === FIM DO FIX ===

  // ==============================================================================
  // 🎛️ FILTROS DE STATUS
  // ==============================================================================
  const statusFilters = useMemo(
    () => [
      {
        value: '',
        label: t('sharedAll'),
        count: stats.total,
        color: theme.palette.primary.main,
        Icon: PeopleIcon,
      },
      {
        value: 'active',
        label: 'Ativos',
        count: stats.active,
        color: theme.palette.success.main,
        Icon: CheckCircleIcon,
      },
      {
        value: 'disabled',
        label: 'Inativos',
        count: stats.disabled,
        color: theme.palette.error.main,
        Icon: ErrorIcon,
      },
    ],
    [stats, t, theme]
  );

  const formatExpirationDate = (expirationTime) => {
    if (!expirationTime) return '';

    try {
      const dateOnly = expirationTime.slice(0, 10);
      return dayjs(dateOnly).format(window.__TRACCAR_DATE_FORMAT);
    } catch (error) {
      console.error('Error formatting date:', error);
      return expirationTime;
    }
  };

  // ==============================================================================
  // 🎬 RENDERIZAÇÃO
  // ==============================================================================
  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedUsers']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, minHeight: 0, px: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* CARDS DE ESTATÍSTICAS */}
          {/* ═══════════════════════════════════════════════════════ */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, mb: 2 }}>
            {statusFilters.map((filter) => (
              <Paper
                key={filter.value}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(0);
                }}
                sx={{
                  flex: '1 1 200px',
                  minWidth: 200,
                  p: 1.5,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: statusFilter === filter.value
                    ? filter.color
                    : 'divider',
                  bgcolor: statusFilter === filter.value
                    ? filter.color + '10'
                    : 'background.paper',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                    borderColor: filter.color,
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: filter.color + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <filter.Icon sx={{ color: filter.color, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {filter.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {filter.label}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* BARRA DE AÇÕES */}
          {/* ═══════════════════════════════════════════════════════ */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mb: 2,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* Campo de busca - ESQUERDA */}
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              placeholder={t('sharedSearch')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.text.secondary
                            : theme.palette.primary.dark,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                flexGrow: 1,           // ⬅️ Faz o campo expandir até os botões
                mr: 2,                 // ⬅️ Espaço entre campo e botões
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.background.paper,
                borderRadius: 1,
              }}
            />

            {/* Botões de ação - DIREITA */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: { xs: 'center', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<AddCircleIcon />}
                onClick={() => navigate('/settings/user')}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: { xs: '100%', sm: 'auto' },
                }}
              >
                {t('sharedAdd')}
              </Button>
              {isAdmin && (
                <>
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
                    CSV
                  </Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>
                    Excel
                  </Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
                    PDF
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* ═══════════════════════════════════════════════════════ */}
          {/* TABELA */}
          {/* ═══════════════════════════════════════════════════════ */}
          <Paper
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 80,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('sharedName')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('userEmail')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 100,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Admin
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 120,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('deviceStatus')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 180,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('userExpirationTime')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        width: 200,
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : theme.palette.primary.light,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {t('actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginatedItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedNoData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedItems.map((row) => (
                      <TableRow hover key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          {row.administrator ? (
                            <Chip
                              label="Sim"
                              size="small"
                              sx={{
                                bgcolor: theme.palette.info.main,
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 24,
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Não
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.disabled ? 'Inativo' : 'Ativo'}
                            size="small"
                            sx={{
                              bgcolor: row.disabled
                                ? theme.palette.error.main
                                : theme.palette.success.main,
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              height: 24,
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatExpirationDate(row.expirationTime)}</TableCell>
                                {/* Ícone de conexões */}
                                <TableCell>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    {/* Ações padrão com ícones coloridos */}
                                    <CollectionActions
                                      itemId={row.id}
                                      editPath="/settings/user"
                                      endpoint="users"
                                      setTimestamp={setTimestamp}
                                      customActions={[
                                        {
                                          key: 'connections',
                                          title: t('sharedConnections'),
                                          icon: <LinkIcon sx={{ color: theme.palette.info.main }} fontSize="small" />,
                                          handler: (id) => navigate(`/settings/user/${id}/connections`),
                                        },
                                      ]}
                                    />

                                    {/* Ação extra: login como usuário */}
                                    {isAdmin && row.id !== currentUser.id && (
                                      <Button
                                        size="small"
                                        onClick={() => handleLogin(row.id)}
                                        title={t('loginLogin')}
                                        sx={{
                                          minWidth: 0,
                                          p: 0.5,
                                          color: theme.palette.success.main,
                                          '&:hover': {
                                            color: theme.palette.success.dark,
                                          },
                                        }}
                                      >
                                        <LoginIcon fontSize="small" />
                                      </Button>
                                    )}
                                  </Stack>
                                </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ═══════════════════════════════════════════════════════ */}
            {/* PAGINAÇÃO */}
            {/* ═══════════════════════════════════════════════════════ */}
            <TablePagination
              component="div"
              count={filteredAndSearchedItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Por pág.:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,

                // Select de linhas
                '.MuiTablePagination-select': {
                  width: '60px',
                  minWidth: '60px',
                  fontSize: '0.875rem',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                  borderRadius: '2px',
                },

                // Label "Por pág.:"
                '.MuiTablePagination-selectLabel': {
                  fontSize: '0.875rem',
                  margin: 0,
                  marginBottom: 0,
                },

                // Texto "1–10 de 100"
                '.MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem',
                  margin: 0,
                  marginBottom: 0,
                },

                // Botões < >
                '.MuiTablePagination-actions': {
                  marginLeft: '8px',
                  '& .MuiIconButton-root': {
                    padding: '4px',
                  },
                },

                // Toolbar container
                '.MuiTablePagination-toolbar': {
                  minHeight: '52px',
                  paddingLeft: '16px',
                  paddingRight: '8px',
                },
              }}
            />
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default UsersPage;
